import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { url } from '../../helper/url_helper';

const PDFViewer = ({ preSignedUrl }) => {
    const containerRef = useRef(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const imgRef = useRef(null);

    // Update this to your Spring Boot server URL
    const API_BASE_URL = url;

    // Detect mobile
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fetch PDF metadata (page count)
    useEffect(() => {
        const fetchPdfMetadata = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await axios.get(`${API_BASE_URL}metadata`, {
                    params: { pdfUrl: preSignedUrl }
                });

                setNumPages(response.data.pageCount);
                setIsLoading(false);
            } catch (err) {
                console.error('Error fetching PDF metadata:', err);
                setError('Failed to load PDF metadata');
                setIsLoading(false);
            }
        };

        fetchPdfMetadata();
    }, [preSignedUrl]);

    // Render the current page
    useEffect(() => {
        const renderPage = async () => {
            if (!numPages) return; // Don't render if we don't know total pages

            try {
                setIsLoading(true);
                setError(null);

                const scale = isFullScreen ? 2 : 1.5;
                const encodedPdfUrl = encodeURIComponent(preSignedUrl);
                const imageUrl = `${API_BASE_URL}render?pdfUrl=${encodedPdfUrl}&pageNumber=${pageNumber}&scale=${scale}`;

                // Create a new image element if none exists
                if (!imgRef.current) {
                    const img = new Image();
                    imgRef.current = img;
                }

                // Set up event handlers
                imgRef.current.onload = () => setIsLoading(false);
                imgRef.current.onerror = () => {
                    setError('Failed to load PDF page');
                    setIsLoading(false);
                };

                // Set the source to trigger the load
                imgRef.current.src = imageUrl;

            } catch (err) {
                console.error('Error rendering PDF page:', err);
                setError('Failed to render PDF page');
                setIsLoading(false);
            }
        };

        renderPage();

        // Cleanup function
        return () => {
            if (imgRef.current) {
                imgRef.current.onload = null;
                imgRef.current.onerror = null;
            }
        };
    }, [preSignedUrl, pageNumber, numPages, isFullScreen]);

    // Navigation functions
    const goToPrevPage = () => {
        if (pageNumber <= 1) return;
        setPageNumber(pageNumber - 1);
    };

    const goToNextPage = () => {
        if (pageNumber >= numPages) return;
        setPageNumber(pageNumber + 1);
    };

    // Fullscreen toggle
    const toggleFullScreen = () => {
        if (!isFullScreen) {
            if (containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen();
            } else if (containerRef.current.webkitRequestFullscreen) {
                containerRef.current.webkitRequestFullscreen();
            } else if (containerRef.current.msRequestFullscreen) {
                containerRef.current.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    // Handle fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                height: isFullScreen ? '100vh' : isMobile ? 'calc(100vh - 70px)' : '80vh',
                overflow: 'hidden',
                userSelect: 'none',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column'
            }}
            onContextMenu={(e) => e.preventDefault()}
        >
            {/* Header with navigation controls */}
            <div style={{
                backgroundColor: '#2c3e50',
                color: 'white',
                padding: isMobile ? '8px 10px' : '12px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 100,
                flexWrap: isMobile ? 'wrap' : 'nowrap'
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isMobile ? '5px' : '15px',
                    flex: isMobile ? 1 : 'auto'
                }}>
                    <button
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                        style={{
                            padding: isMobile ? '6px 8px' : '8px 16px',
                            backgroundColor: pageNumber <= 1 ? '#95a5a6' : '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: isMobile ? '0.8rem' : '1rem'
                        }}
                    >
                        <svg width={isMobile ? "12" : "16"} height={isMobile ? "12" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        {isMobile ? 'Prev' : 'Previous'}
                    </button>

                    <button
                        onClick={goToNextPage}
                        disabled={pageNumber >= numPages}
                        style={{
                            padding: isMobile ? '6px 8px' : '8px 16px',
                            backgroundColor: pageNumber >= numPages ? '#95a5a6' : '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: pageNumber >= numPages ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: isMobile ? '0.8rem' : '1rem'
                        }}
                    >
                        {isMobile ? 'Next' : 'Next'}
                        <svg width={isMobile ? "12" : "16"} height={isMobile ? "12" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '5px' : '15px',
                    flex: isMobile ? 1 : 'auto',
                    justifyContent: isMobile ? 'center' : 'flex-end'
                }}>
                    <div style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        padding: isMobile ? '4px 8px' : '6px 12px',
                        borderRadius: '20px',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                    }}>
                        P. <span style={{ fontWeight: 'bold' }}>{pageNumber}</span> / <span style={{ fontWeight: 'bold' }}>{numPages || '--'}</span>
                    </div>

                    <button
                        onClick={toggleFullScreen}
                        style={{
                            padding: isMobile ? '6px 8px' : '8px 16px',
                            backgroundColor: '#16a085',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: isMobile ? '0.8rem' : '1rem'
                        }}
                    >
                        {isFullScreen ? (
                            <>
                                <svg width={isMobile ? "12" : "16"} height={isMobile ? "12" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 20h12M6 4h12M4 8v8m16-8v8" />
                                </svg>
                                {isMobile ? 'Exit' : 'Exit Fullscreen'}
                            </>
                        ) : (
                            <>
                                <svg width={isMobile ? "12" : "16"} height={isMobile ? "12" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                                {isMobile ? 'Full' : 'Fullscreen'}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* PDF Image Container */}
            <div style={{
                flex: 1,
                position: 'relative',
                overflow: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                padding: isFullScreen ? '40px' : isMobile ? '10px' : '20px'
            }}>
                {isLoading ? (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            width: isMobile ? '30px' : '50px',
                            height: isMobile ? '30px' : '50px',
                            border: '5px solid #f3f3f3',
                            borderTop: '5px solid #3498db',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 20px'
                        }} />
                        <p style={{ color: '#7f8c8d', fontWeight: '500', fontSize: isMobile ? '0.9rem' : '1rem' }}>Loading page {pageNumber}...</p>
                    </div>
                ) : error ? (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center'
                    }}>
                        <p style={{ color: '#e74c3c', fontWeight: '500', fontSize: isMobile ? '0.9rem' : '1rem' }}>{error}</p>
                        <button
                            onClick={() => {
                                setPageNumber(1);
                                setError(null);
                            }}
                            style={{
                                marginTop: '10px',
                                padding: isMobile ? '6px 12px' : '8px 16px',
                                backgroundColor: '#3498db',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: isMobile ? '0.8rem' : '1rem'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                ) : (
                    imgRef.current && (
                        <img
                            src={imgRef.current.src}
                            alt={`PDF Page ${pageNumber}`}
                            style={{
                                maxWidth: '100%',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                backgroundColor: 'white'
                            }}
                        />
                    )
                )}
            </div>

            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};

export default PDFViewer;